import clsx from "clsx";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ContentDivider } from "@atoms/contentDivider";
import { Icon } from "@atoms/icon";
import { Typography } from "@atoms/typography";
import { FastButton } from "@components/fastFields/fastButton";
import { FastLinkButton } from "@components/fastFields/fastLinkButton";
import { FastPassword } from "@components/fastFields/fastPassword/fastPassword";
import { FastPhone } from "@components/fastFields/fastPhone";
import { Row } from "@layout/row";
import { IButtonModeEnum } from "@molecules/button";
import { FormLayout } from "@organisms/formLayout";
import {
  loginFormInitialValues,
  loginFormValidationSchema,
} from "@templates/authManagement/loginForm/schema";
import { zodResolver } from "@utils/zod";

import classes from "./styles.module.scss";

import type { IFormTemplateProps } from "@interfaces";
import type { ILoginFormSchema } from "@templates/authManagement/loginForm/schema";

export function LoginForm({
  isLoading,
  onSubmit,
  initialValues,
}: IFormTemplateProps<ILoginFormSchema>) {
  const { t } = useTranslation("page");

  const { control, handleSubmit } = useForm<ILoginFormSchema>({
    defaultValues: initialValues || loginFormInitialValues,
    resolver: zodResolver(loginFormValidationSchema),
    mode: "onChange",
  });

  return (
    <div className={clsx(classes.loginForm)}>
      <div className={clsx(classes.loginForm__head)}>
        <div className={clsx(classes.loginForm__circleOutside)}>
          <div className={clsx(classes.loginForm__circle)}>
            <Icon name={"UserLine"} fontSize={"fs-32"} color={"icon-sub-600"} />
          </div>
        </div>
        <Typography fontType={"label-x-large"}>
          {t("auth.login.title")}
        </Typography>
        <Typography fontType={"paragraph-medium"} themeColor={"text-sub-600"}>
          {t("auth.login.description")}
        </Typography>
      </div>
      <ContentDivider className={"w-100"} />
      <FormLayout
        noButtons
        // ]}
        onSubmit={handleSubmit(onSubmit)}
        isLoading={isLoading}
        noMarginTop
      >
        <Row noBorder>
          <Row.Input>
            <FastPhone
              type={"phoneNumber"}
              control={control}
              name={"phone"}
              isRequired
            />
          </Row.Input>
        </Row>
        <Row noBorder>
          <Row.Input>
            <FastPassword
              type={"password"}
              control={control}
              name={"password"}
              isRequired
            />
          </Row.Input>
        </Row>
        <div className={classes.loginForm__linkContainer}>
          <FastLinkButton type={"forgetPassword"} />
        </div>
        <FastButton
          type={"login"}
          mode={IButtonModeEnum.Submit}
          isLoading={isLoading}
        />
      </FormLayout>
    </div>
  );
}
